import {
  PreviewHeading,
  ProcessedClauseChange,
} from "common/flowmaster/types/amendment";
import {ClauseAtom, Clausepart} from "common/types/document";
import buildUnitPreview, {
  getUnitAtoms,
} from "./amendment_units/build_unit_preview";
import buildMapPreview, {
  getMapPreview,
  isMapPreviewEnabled,
  mapUnitKey,
  mapUnitLabel,
} from "./amendment_units/map_preview";
import {
  formatUnitLabel,
  getUnitKey,
  isInUnit,
  normaliseReference,
} from "./amendment_units/unit_key";

interface Unit {
  key: string;
  sectionId: unknown;
  atoms: ClauseAtom[];
  changes: ProcessedClauseChange[];
  order: number;
}

const MIN_SNIPPET_LENGTH = 12;

function normaliseText(text: string | undefined): string {
  return (text ?? "").replace(/\s+/g, " ").trim().toLowerCase();
}

function anchorSnippets(change: ProcessedClauseChange): string[] {
  return [
    change.deleted_text,
    change.replacement_context?.before,
    change.replacement_context?.after,
  ]
    .map(normaliseText)
    .filter(snippet => snippet.length >= MIN_SNIPPET_LENGTH)
    .sort((a, b) => b.length - a.length);
}

// The same reference can appear in several sections (main, schedules);
// prefer the section whose text holds the amendment's anchor text
function pickBySection(
  atoms: ClauseAtom[],
  candidates: ClauseAtom[],
  change: ProcessedClauseChange,
): ClauseAtom {
  const sectionIds = Array.from(
    new Set(candidates.map(atom => atom.section_id)),
  );
  if (sectionIds.length <= 1) {
    return candidates[0];
  }
  const sectionTexts = sectionIds.map(sectionId =>
    atoms
      .filter(atom => atom.section_id === sectionId)
      .map(atom => normaliseText(atom.text))
      .join(" "),
  );
  for (const snippet of anchorSnippets(change)) {
    const index = sectionTexts.findIndex(text => text.includes(snippet));
    if (index !== -1) {
      const sectionId = sectionIds[index];
      return candidates.find(
        atom => atom.section_id === sectionId,
      ) as ClauseAtom;
    }
  }
  return candidates[0];
}

function findAnchorAtom(
  atoms: ClauseAtom[],
  change: ProcessedClauseChange,
  unitKey: string,
): ClauseAtom | undefined {
  if (change.clause_id) {
    const byId = atoms.find(atom => String(atom.id) === change.clause_id);
    if (byId) {
      return byId;
    }
  }
  const reference = normaliseReference(change.clause_reference);
  const byReference = atoms.filter(
    atom => normaliseReference(atom.reference) === reference,
  );
  const candidates =
    byReference.length > 0
      ? byReference
      : atoms.filter(atom => normaliseReference(atom.reference) === unitKey);
  if (candidates.length === 0) {
    return undefined;
  }
  return pickBySection(atoms, candidates, change);
}

interface ResolvedUnit {
  unitKey: string;
  anchor: ClauseAtom | undefined;
}

// An insert belongs to the clause its new number sits in (3.1(b) -> 3.1),
// even when its anchor line is the next clause's heading. The target unit
// must hold the insert's context text: a new 25.5 inserted after 25.4(f)
// displaces the old 25.5 and stays with 25.4
function resolveInsertUnit(
  atoms: ClauseAtom[],
  change: ProcessedClauseChange,
): ResolvedUnit | null {
  const target = change.rawAmendment?.target_clause_reference;
  const isInsert = change.insert_after || change.insert_before;
  if (!isInsert || !target) {
    return null;
  }
  const unitKey = getUnitKey(target);
  if (!unitKey || unitKey === getUnitKey(change.clause_reference)) {
    return null;
  }
  // A target that already exists is displaced by the insert, which then
  // stays with its anchor clause
  const targetReference = normaliseReference(target);
  if (
    atoms.some(atom => normaliseReference(atom.reference) === targetReference)
  ) {
    return null;
  }
  const candidates = atoms.filter(
    atom =>
      isInUnit(atom.reference, unitKey) && (atom.text || "").trim().length > 0,
  );
  if (candidates.length === 0) {
    return null;
  }
  const unitText = candidates.map(atom => normaliseText(atom.text)).join(" ");
  const holdsContext = anchorSnippets(change).some(snippet =>
    unitText.includes(snippet),
  );
  if (!holdsContext) {
    return null;
  }
  return {unitKey, anchor: pickBySection(atoms, candidates, change)};
}

function resolveUnit(
  atoms: ClauseAtom[],
  change: ProcessedClauseChange,
): ResolvedUnit {
  const byTarget = resolveInsertUnit(atoms, change);
  if (byTarget) {
    return byTarget;
  }
  const unitKey = getUnitKey(change.clause_reference);
  return {
    unitKey,
    anchor: unitKey ? findAnchorAtom(atoms, change, unitKey) : undefined,
  };
}

// One card per numbered sub-clause, previewed as a redline of the whole unit
// Cards from the amendments' own working-set lines
function buildMapCards(
  atoms: ClauseAtom[],
  changes: ProcessedClauseChange[],
): ProcessedClauseChange[] {
  const groups = new Map<string, ProcessedClauseChange[]>();
  changes.forEach(change => {
    const key = mapUnitKey(change);
    groups.set(key, [...(groups.get(key) ?? []), change]);
  });
  return Array.from(groups.values())
    .sort(
      (a, b) =>
        (getMapPreview(a[0])?.line ?? 0) - (getMapPreview(b[0])?.line ?? 0),
    )
    .map(group => {
      const first = group[0];
      const preview = buildMapPreview(group);
      const linkUnit = getUnitKey(first.clause_reference);
      const linkAtom = linkUnit
        ? findAnchorAtom(atoms, first, linkUnit)
        : undefined;
      return {
        ...first,
        clause_id: linkAtom ? String(linkAtom.id) : "",
        clause_reference: mapUnitLabel(first),
        section_reference: getMapPreview(first)?.part_id,
        insert_after: group.every(
          change => change.insert_after || change.insert_before,
        ),
        insert_before: false,
        delete: group.every(change => change.delete),
        grouped: true,
        original_text: (getMapPreview(first)?.unit_lines ?? [])
          .map(line => line.text)
          .join("\n"),
        modified_text: undefined,
        related_changes: group,
        unit_preview: preview,
      };
    });
}

const groupClauseAmendments = (
  allAmendments: ProcessedClauseChange[],
  clauseParts: Clausepart[],
  headings: PreviewHeading[] = [],
): ProcessedClauseChange[] => {
  const atoms = (clauseParts as unknown) as ClauseAtom[];
  const units = new Map<string, Unit>();
  const ungrouped: ProcessedClauseChange[] = [];

  const useMap = isMapPreviewEnabled();
  const mapChanges = allAmendments.filter(
    change => useMap && getMapPreview(change) !== null,
  );
  const mapCards = buildMapCards(atoms, mapChanges);
  const amendments = allAmendments.filter(
    change => !mapChanges.includes(change),
  );

  amendments.forEach(change => {
    const {unitKey, anchor} = resolveUnit(atoms, change);
    if (!anchor) {
      ungrouped.push(change);
      return;
    }
    const sectionId = anchor.section_id;
    const mapKey = `${String(sectionId)}|${unitKey}`;
    let unit = units.get(mapKey);
    if (!unit) {
      const unitAtoms = getUnitAtoms(atoms, unitKey, sectionId);
      unit = {
        key: unitKey,
        sectionId,
        atoms: unitAtoms,
        changes: [],
        order: atoms.indexOf(unitAtoms[0] ?? anchor),
      };
      units.set(mapKey, unit);
    }
    unit.changes.push(change);
  });

  const cards = Array.from(units.values())
    .sort((a, b) => a.order - b.order)
    .map((unit): ProcessedClauseChange | null => {
      const first = unit.changes[0];
      const unitHeadings = headings.filter(
        heading =>
          heading.section_id === undefined ||
          unit.sectionId === undefined ||
          heading.section_id === unit.sectionId,
      );
      const preview = buildUnitPreview(
        unit.key,
        unit.atoms,
        unit.changes,
        unitHeadings,
      );
      // Nothing placed means the anchor clause is wrong; show the edits plain
      if (preview.unplaced.length === unit.changes.length) {
        const label = formatUnitLabel(unit.key);
        ungrouped.push(
          ...unit.changes.map(change => ({...change, clause_reference: label})),
        );
        return null;
      }
      const allDelete = unit.changes.every(change => change.delete);
      const allInsert = unit.changes.every(
        change => change.insert_after || change.insert_before,
      );
      return {
        ...first,
        clause_id: String(unit.atoms[0]?.id ?? first.clause_id),
        clause_reference: formatUnitLabel(unit.key),
        insert_after: allInsert,
        insert_before: false,
        delete: allDelete,
        grouped: true,
        original_text: unit.atoms.map(atom => atom.text || "").join("\n"),
        modified_text: undefined,
        related_changes: unit.changes,
        unit_preview: preview,
      };
    })
    .filter((card): card is ProcessedClauseChange => card !== null);

  return [...mapCards, ...cards, ...ungrouped];
};

export default groupClauseAmendments;
